import React, { useState, useEffect, useRef, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Cookies from 'js-cookie';
import './DeleteAccountVerification.css';
import config from '../config';
import { UserContext } from '../contexts/UserContext';

const DeleteAccountVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { maskedPhone } = location.state || {};
  const { logout } = useContext(UserContext); // Get logout function from context

  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [password, setPassword] = useState(''); // User must type password again for security
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes in seconds
  const [resendCountdown, setResendCountdown] = useState(60); // 60 seconds countdown for resend
  const [attemptsLeft, setAttemptsLeft] = useState(3);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success' or 'error'
  const [isResending, setIsResending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  const inputRefs = useRef([
    React.createRef(),
    React.createRef(),
    React.createRef(),
    React.createRef(),
    React.createRef(),
    React.createRef()
  ]);

  useEffect(() => {
    // Focus first input on mount
    if (inputRefs.current[0]?.current) {
      inputRefs.current[0].current.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Redirect if no masked phone (direct access without going through DeleteAccount)
    if (!maskedPhone) {
      navigate('/delete-account');
      return;
    }

    // Countdown timer for code expiration
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [timeLeft, maskedPhone, navigate]);

  useEffect(() => {
    // Countdown timer for resend button
    if (resendCountdown > 0) {
      const timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleCodeChange = (index, value) => {
    // Only allow numbers
    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-focus next input
    if (value && index < 5 && inputRefs.current[index + 1]?.current) {
      inputRefs.current[index + 1].current.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.current.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    
    if (!/^\d+$/.test(pastedData)) return;

    const newCode = [...code];
    for (let i = 0; i < pastedData.length && i < 6; i++) {
      newCode[i] = pastedData[i];
    }
    setCode(newCode);

    // Focus last filled input or next empty
    const nextIndex = Math.min(pastedData.length, 5);
    if (inputRefs.current[nextIndex]?.current) {
      inputRefs.current[nextIndex].current.focus();
    }
  };

  const handleResendCode = async () => {
    if (!password) {
      setMessage('Veuillez entrer votre mot de passe pour renvoyer le code');
      setMessageType('error');
      return;
    }

    setIsResending(true);
    setMessage('');

    try {
      const token = Cookies.get('authToken');
      await axios.post(
        `${config.backendUrl}/users/account/request-deletion/`,
        {
          password: password
        },
        {
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      setMessage('Un nouveau code a été envoyé à votre téléphone');
      setMessageType('success');
      setTimeLeft(300);
      setResendCountdown(60); // Reset countdown to 60 seconds
      setAttemptsLeft(3);
      setCode(['', '', '', '', '', '']);
      
      if (inputRefs.current[0]?.current) {
        inputRefs.current[0].current.focus();
      }
    } catch (error) {
      setMessage(
        error.response?.data?.message ||
        error.response?.data?.error ||
        'Erreur lors de l\'envoi du code'
      );
      setMessageType('error');
    } finally {
      setIsResending(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();

    const verificationCode = code.join('');
    
    if (verificationCode.length !== 6) {
      setMessage('Veuillez entrer le code à 6 chiffres');
      setMessageType('error');
      return;
    }

    if (!password) {
      setMessage('Veuillez entrer votre mot de passe');
      setMessageType('error');
      return;
    }

    setIsVerifying(true);
    setMessage('');

    try {
      const token = Cookies.get('authToken');
      await axios.post(
        `${config.backendUrl}/users/account/confirm-deletion/`,
        {
          code: verificationCode,
          password: password,
        },
        {
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      // Account deleted successfully - use logout to properly clean up user context
      setMessage('Votre compte a été supprimé avec succès');
      setMessageType('success');

      // Wait 2 seconds to show success message, then logout (which clears everything and redirects)
      setTimeout(async () => {
        await logout();
      }, 2000);
    } catch (error) {
      const errorData = error.response?.data;
      
      if (errorData?.attempts_left !== undefined) {
        setAttemptsLeft(errorData.attempts_left);
      }

      setMessage(
        errorData?.message ||
        errorData?.error ||
        'Erreur lors de la vérification'
      );
      setMessageType('error');

      // Clear code on error
      setCode(['', '', '', '', '', '']);
      if (inputRefs[0].current) {
        inputRefs[0].current.focus();
      }
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className="delete-account-verification">
      <div className="verification-container">
        <div className="verification-header">
          <h1>Vérification de suppression</h1>
          <button 
            className="back-button"
            onClick={() => navigate('/delete-account')}
          >
            ← Retour
          </button>
        </div>

        <div className="verification-content">
          <div className="info-box">
            <p>
              Un code de vérification à 6 chiffres a été envoyé au{' '}
              <strong>{maskedPhone}</strong>
            </p>
          </div>

          {message && (
            <div className={`message-box ${messageType}`}>
              {message}
            </div>
          )}

          <form onSubmit={handleVerify}>
            <div className="form-group">
              <label>Code de vérification</label>
              {isVerifying ? (
                <div className="skeleton-code-container">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="skeleton skeleton-code-input"></div>
                  ))}
                </div>
              ) : (
                <div className="code-input-container">
                  {code.map((digit, index) => (
                    <input
                      key={index}
                      ref={inputRefs.current[index]}
                      type="text"
                      maxLength="1"
                      value={digit}
                      onChange={(e) => handleCodeChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      onPaste={index === 0 ? handlePaste : undefined}
                      className="code-digit-input"
                      disabled={isVerifying}
                    />
                  ))}
                </div>
              )}
            </div>

            <div className="timer-attempts">
              <div className="timer">
                Temps restant: <strong>{formatTime(timeLeft)}</strong>
              </div>
              <div className="attempts">
                Tentatives restantes: <strong>{attemptsLeft}</strong>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="password">Mot de passe</label>
              {isVerifying ? (
                <div className="skeleton skeleton-input"></div>
              ) : (
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Entrez votre mot de passe"
                  required
                  disabled={isVerifying}
                />
              )}
            </div>

            <div className="warning-box">
              <p>
                ⚠️ <strong>Attention:</strong> Cette action est irréversible. Une
                fois votre compte supprimé, toutes vos données seront définitivement
                perdues.
              </p>
            </div>

            <div className="button-group">
              <button
                type="button"
                onClick={handleResendCode}
                disabled={isResending || resendCountdown > 0 || isVerifying}
                className="secondary-button"
              >
                {isResending 
                  ? 'Envoi...' 
                  : resendCountdown > 0 
                    ? `Renvoyer dans ${resendCountdown}s` 
                    : 'Renvoyer le code'}
              </button>

              {isVerifying ? (
                <div className="skeleton skeleton-button"></div>
              ) : (
                <button
                  type="submit"
                  disabled={isVerifying || code.join('').length !== 6 || !password}
                  className="danger-button"
                >
                  {isVerifying ? 'Vérification...' : 'Supprimer mon compte'}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DeleteAccountVerification;
